import React, { useEffect, useState, useRef } from "react";
import "../styles/executive-responsive.css";
import { authFetch } from "../../../services/authFetch";
import config from "../../../config";
import { getParticipantColor } from "../../../utils/participantColors";

interface Props {
  citation: { id: string; text: string; x: number; y: number };
}

// Simple in-memory cache to avoid re-fetching on re-hover
const cache = new Map<string, any>();

// Purge stale cache entries that were cached before we added rundown data
for (const [k, v] of cache) {
  if (v && !("rundown" in v)) cache.delete(k);
}

export const CitationHoverCard: React.FC<Props> = ({ citation }) => {
  const [result, setResult] = useState<any>(cache.get(citation.id) || null);
  const [loading, setLoading] = useState(!cache.has(citation.id));

  // Extract ID and type
  const prefixMatch = citation.id.match(/^(LOG|TRANS|OTF)-(.+)$/i);
  const typeStr = prefixMatch ? prefixMatch[1].toUpperCase() : "LOG";
  const uuid = prefixMatch ? prefixMatch[2] : citation.id;

  const isOtf = typeStr === "OTF";
  const isTrans = typeStr === "TRANS";

  // Colors matching the inline citation badges
  const typeColor = isOtf
    ? "#06b6d4" // turquoise for OTF
    : isTrans
      ? "#8B5CF6" // purple for TRANS
      : "#EC4899"; // pink for LOG

  const label = isOtf ? "OTF" : isTrans ? "TRANSCRIPCIÓN" : "LOG";

  useEffect(() => {
    // Already cached
    if (cache.has(citation.id)) {
      setResult(cache.get(citation.id));
      setLoading(false);
      return;
    }

    let cancelled = false;
    async function load() {
      try {
        const res = await authFetch(
          `${config.API_URL}/api/citation-detail/${uuid}?type=${typeStr}`,
        );
        if (res.ok) {
          const data = await res.json();
          // Build formatted participants
          const participants = (data.participants || []).map((s: any) => {
            const cs = getParticipantColor(s.color_id, s.custom_color);
            return {
              id: s.id,
              name: s.alias || `${s.first_name || ""} ${s.last_name || ""}`.trim(),
              bg: cs.bg,
              border: cs.border,
              textColor: cs.text,
            };
          });

          const formatted = {
            text: data.log?.text || data.segment?.text || citation.text,
            authorName: data.log?.author_name || null,
            time: data.log?.created_at || data.segment?.start_at,
            story: data.story || null,
            event: data.event || null,
            rundown: data.rundown || null,
            participants,
            tags: data.tags || [],
          };

          cache.set(citation.id, formatted);
          if (!cancelled) {
            setResult(formatted);
            setLoading(false);
          }
        } else {
          // API returned 404 — still show the glossary text if we have it
          const fallback = {
            text: citation.text,
            authorName: null,
            time: null,
            story: null,
            event: null,
            rundown: null,
            participants: [],
            tags: [],
          };
          cache.set(citation.id, fallback);
          if (!cancelled) {
            setResult(fallback);
            setLoading(false);
          }
        }
      } catch (e) {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [citation.id]);

  const displayText = result?.text || citation.text;
  const authorName = result?.authorName;
  const time = result?.time;
  const story = result?.story;
  const rundown = result?.rundown;
  const participants = result?.participants || [];
  const tags = result?.tags || [];

  return (
    <div
      className="citation-hover-card"
      style={{
        position: "fixed",
        top: citation.y,
        left: citation.x,
        transform: "translate(-50%, -100%)",
        background: "rgba(30, 30, 35, 0.98)",
        backdropFilter: "blur(12px)",
        border: `1px solid ${typeColor}`,
        padding: "12px 14px",
        borderRadius: "8px",
        color: "#eee",
        fontSize: "0.8rem",
        width: 340,
        pointerEvents: "none",
        zIndex: 9999,
        boxShadow: `0 8px 24px rgba(0,0,0,0.6), 0 0 12px ${typeColor}30`,
        lineHeight: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      {/* Header: type badge + time + story */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          paddingBottom: "6px",
          marginBottom: "2px",
        }}
      >
        <span
          style={{
            fontSize: "0.65rem",
            fontWeight: 800,
            color: typeColor,
            background: `${typeColor}20`,
            padding: "2px 8px",
            borderRadius: "4px",
            letterSpacing: "0.5px",
          }}
        >
          {label}
        </span>

        {loading ? (
          <span style={{ fontSize: "0.65rem", color: "var(--color-text-muted)", opacity: 0.5 }}>
            cargando...
          </span>
        ) : time ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
            <span style={{ fontSize: "0.65rem", color: "var(--color-text-muted)" }}>
              {new Date(time).toLocaleTimeString("es-MX", {
                timeZone: "America/Mexico_City",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </span>
            {(rundown || story) && (
              <span
                style={{
                  fontSize: "0.55rem",
                  color: "var(--color-text-muted)",
                  textAlign: "right",
                  lineHeight: 1.3,
                }}
              >
                {rundown?.name || ""}
                {rundown?.name ? " · " : ""}
                {new Date(story?.start_at || time).toLocaleDateString("es-MX", {
                  timeZone: "America/Mexico_City",
                })}
              </span>
            )}
            {story?.title && (
              <span
                style={{ fontSize: "0.55rem", color: typeColor, textAlign: "right", opacity: 0.8 }}
              >
                {story.title}
              </span>
            )}
          </div>
        ) : null}
      </div>

      {loading ? (
        /* Skeleton while loading */
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <div
            style={{
              background: "rgba(255,255,255,0.06)",
              height: "10px",
              width: "60%",
              borderRadius: "4px",
            }}
          />
          <div
            style={{
              background: "rgba(255,255,255,0.06)",
              height: "10px",
              width: "90%",
              borderRadius: "4px",
            }}
          />
          <div
            style={{
              background: "rgba(255,255,255,0.06)",
              height: "10px",
              width: "75%",
              borderRadius: "4px",
            }}
          />
        </div>
      ) : (
        <>
          {/* Author (logger user) */}
          {authorName && (
            <div
              style={{
                fontSize: "0.65rem",
                color: "var(--color-text-muted)",
                marginBottom: "-4px",
              }}
            >
              Registrado por: {authorName}
            </div>
          )}

          {/* Main text */}
          <div>{displayText}</div>

          {/* Participants — colored pills matching the logger */}
          {participants.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginTop: "2px" }}>
              {participants.map((p: any) => (
                <span
                  key={p.id}
                  style={{
                    background: p.bg,
                    color: p.textColor,
                    border: `1px solid ${p.border}`,
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    padding: "2px 8px",
                    borderRadius: "10px",
                    letterSpacing: "0.5px",
                  }}
                >
                  {p.name.toUpperCase()}
                </span>
              ))}
            </div>
          )}

          {/* Tags */}
          {tags.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginTop: "2px" }}>
              {tags.map((tag: any) => (
                <span
                  key={tag.id}
                  style={{
                    background: tag.color ? `${tag.color}20` : "rgba(255,255,255,0.08)",
                    border: `1px solid ${tag.color || "rgba(255,255,255,0.15)"}`,
                    color: tag.color || "var(--color-text-muted)",
                    fontSize: "0.6rem",
                    padding: "2px 6px",
                    borderRadius: "10px",
                  }}
                >
                  {tag.name}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
